// @flow
import { CRUISE_BOOKING } from 'Client/cms/toolkit/constants';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { buildProductArgs, buildCruiseArgs } from 'Utils/graphql';
import { LINE_CRUISE_BOOKING } from 'Utils/request/requestDictionary';

type CriteriaCruiseType = {
  title: string,
  dateLabel: string,
  dateValuePlaceholder: string,
  transportationLabel: string,
  fromToLabel: string,
  fromToValuePlaceholder: string,
  durationLabel: string,
  durationValuePlaceholder: string,
};

export type ResolvedProps = {
  bookingCriteria: {
    initialNumberOfAdults: number,
    initialNumberOfChildren: number,
    departureCities: SelectProps,
  },
  config?: CMSContent,
  cruise: {
    fromToDestination: string,
    departureDate: string,
  },
  product: {
    id: string,
    adultOnly: boolean,
    duration: string,
    dateRange: string,
    minAuthorizedAge: {
      formattedValue: string,
    },
  },
};

export type LineProps = {
  booking?: {
    labelCTA: string,
    disclaimer: string,
    loadingPrice: string,
  },
  criteria?: CriteriaCruiseType,
  dateFormat?: string,
  id?: string,
  pax?: {
    title: string,
    minAgeInformation: string,
    minAgeErrorMessage: string,
  },
  resortId: string,
  title?: string,
};

export const cruiseBookingResolver = {
  name: CRUISE_BOOKING,
  resolve(
    appContext: AppContext,
    props: LineProps,
    pageContext: PageContext,
    location: RouterLocation<CruiseUrlParams, {}>,
  ): Promise<ResolvedProps> {
    const {
      resortId,
      criteria = {},
      dateFormat = '',
      pax = { minAgeErrorMessage: '', minAgeInformation: '' },
    } = props;
    const { minAgeErrorMessage, minAgeInformation } = pax;

    const { cmsContent = '', cmsRelease = '' } = appContext;
    const { urlParams } = location;
    const { durationValuePlaceholder, dateValuePlaceholder, fromToValuePlaceholder } = criteria;
    const productArgs = buildProductArgs(resortId, urlParams);
    const cruiseArgs = buildCruiseArgs(urlParams);
    const cmsArgs = `content : "${cmsContent}" release : "${cmsRelease}"`;

    const query = `{
      product: resort(${productArgs}) {
        id
        adultOnly
        duration(message : "${durationValuePlaceholder}")
        dateRange(message : "${dateValuePlaceholder}" dateFormat : "${dateFormat}")
        minAuthorizedAge {
          formattedValue (message : "${minAgeInformation}" ${cmsArgs})
          value
        }
        minAgeErrorMessage : minAuthorizedAge {
          formattedValue(message : "${minAgeErrorMessage}" ${cmsArgs})
        }
      }
      cruise(${cruiseArgs}) {
        fromToDestination(message : "${fromToValuePlaceholder}")
        departureDate
      }
      bookingCriteria(${productArgs}) {
        departureCities {
          value
          label : name
        },
        initialNumberOfAdults(${productArgs})
        initialNumberOfChildren
      }
      config {
        common {
          participants {
            kidsLabel
            adultsLabel
            kidsAgeLimit
          }
        }
        booking {
          accommodation {
            errorMessages {
              noProposalAvailable
              noTransport
            }
          }
          form {
            dayPlaceholder
            monthPlaceholder
            yearPlaceholder
            childrenBirthdayDateFormatPlaceholder
            errorMaxGuestsLabel
            departureDateErrorMessage
            adultAge
            proposalsLoaderLabel
            errors {
              adultChild
              birthdayFormat
            }
          }
        }
        price {
          format {
            from
            instead
          }
        }
      }
    }`;

    return graphQLClient.runQuery(query, appContext, {
      requestName: LINE_CRUISE_BOOKING,
      strictErrors: false,
    });
  },
};
